import { INVALID_MIN_VERSION_TEXT } from "@klubora/shared";
import { supabase } from "./supabase";
import { readFunctionErrorBody } from "./functionError";

/**
 * KLUB-245 U3. `tenants.min_app_code_version` has no UPDATE grant for any
 * browser role, so the platform writes it through the edge function. `null`
 * clears the minimum (no device is locked out).
 */
export interface SetMinAppCodeVersionResponse {
  updated: true;
  min_app_code_version: number | null;
}

interface SetMinAppCodeVersionErrorBody {
  error?: string;
  message?: string;
  detail?: string;
  min_app_code_version?: number | null;
}

const ERROR_TEXT: Record<string, string> = {
  invalid_min_app_code_version: INVALID_MIN_VERSION_TEXT,
  missing_required_fields: "Requisição incompleta.",
  invalid_tenant_id: "Clube inválido.",
  tenant_not_found: "Clube não encontrado.",
  method_not_allowed: "Requisição inválida.",
  unauthorized: "Sessão expirada. Entre novamente.",
  invalid_token: "Sessão expirada. Entre novamente.",
  not_platform_admin: "Sua conta não tem acesso ao painel Klubora.",
  insufficient_role: "Seu papel no painel não permite alterar a atualização mínima.",
  lookup_failed: "Não foi possível ler o clube. Tente de novo.",
  write_failed: "Não foi possível gravar a atualização mínima. Tente de novo.",
  internal_error: "Erro inesperado ao gravar a atualização mínima. Tente de novo.",
  server_misconfigured: "Erro de configuração do servidor. Avise o time técnico.",
  audit_failed:
    "A atualização mínima foi gravada, mas sem registro de auditoria. Avise o time técnico.",
};

/**
 * Carries `persisted` when the function answered `audit_failed`: the value WAS
 * written, so the UI must show it as saved even though the call failed.
 */
export class SetMinAppCodeVersionError extends Error {
  readonly persisted: number | null | undefined;

  constructor(message: string, persisted?: number | null) {
    super(message);
    this.name = "SetMinAppCodeVersionError";
    this.persisted = persisted;
  }
}

async function toError(error: unknown): Promise<SetMinAppCodeVersionError> {
  const body = await readFunctionErrorBody<SetMinAppCodeVersionErrorBody>(error);
  if (body?.error) {
    const text = ERROR_TEXT[body.error] ?? body.message ?? body.error;
    const persisted = body.error === "audit_failed" ? body.min_app_code_version ?? null : undefined;
    return new SetMinAppCodeVersionError(text, persisted);
  }
  return new SetMinAppCodeVersionError(error instanceof Error ? error.message : String(error));
}

export async function setMinAppCodeVersion(
  tenantId: string,
  minAppCodeVersion: number | null,
): Promise<SetMinAppCodeVersionResponse> {
  const { data, error } = await supabase.functions.invoke<SetMinAppCodeVersionResponse>(
    "platform-set-min-app-code-version",
    { body: { tenant_id: tenantId, min_app_code_version: minAppCodeVersion } },
  );
  if (error) throw await toError(error);
  if (!data) {
    throw new SetMinAppCodeVersionError("platform-set-min-app-code-version returned empty body");
  }
  return data;
}
